import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DeliveryDecision } from '~/components/common/delivery-run-panel';
import { Button } from '~/components/ui/button';

interface Props {
  step: 'decision' | 'preview';
  /** Only meaningful when `step === 'decision'`. */
  decision: DeliveryDecision;
  selectedCount: number;
  totalCount: number;
  reasonValid: boolean;
  isSubmittingReject: boolean;
  productCount: number;
  /** Destination platform display label (e.g. "Google Ads"), for the
   *  Preview step's "will go to {destination}" summary. */
  destinationLabel: string;
  onCancel: () => void;
  onContinue: () => void;
  onRejectSubmit: () => void;
  onBack: () => void;
  onConfirmDeliver: () => void;
  isConfirming: boolean;
}

/**
 * One action bar shared by Decision and Preview, replacing each step's own
 * in-card footer (Decision had Cancel/Continue inside its card; Preview had
 * Back/Confirm at the page bottom) — pinned at the bottom of the wizard's own
 * flex column (a `shrink-0` sibling after the scrolling step content, same
 * pinning technique the header already uses at the top), so it stays visible
 * regardless of how long the active step's content is.
 */
export function DeliveryWizardFooter({
  step,
  decision,
  selectedCount,
  totalCount,
  reasonValid,
  isSubmittingReject,
  productCount,
  destinationLabel,
  onCancel,
  onContinue,
  onRejectSubmit,
  onBack,
  onConfirmDeliver,
  isConfirming,
}: Props) {
  const { t } = useTranslation('common');
  const isReject = step === 'decision' && decision === 'REJECTED';

  // Bordered card mirroring the pinned header card above (same width,
  // radius and surface); `mt-4` keeps it clear of the step content.
  return (
    <footer
      className="bg-background mt-4 flex shrink-0 items-center gap-3 rounded-2xl border px-6 py-4"
      data-testid="delivery-wizard-footer"
    >
      <Button
        type="button"
        variant="outline"
        className="cursor-pointer"
        disabled={step === 'decision' ? isSubmittingReject : isConfirming}
        onClick={step === 'decision' ? onCancel : onBack}
        data-testid={
          step === 'decision' ? 'delivery-wizard-cancel-button' : 'delivery-wizard-back-button'
        }
      >
        {step === 'decision' ? t('deliveryWizard.footer.cancel') : t('deliveryWizard.footer.back')}
      </Button>

      <p
        className="text-muted-foreground ml-auto hidden text-right text-sm sm:block"
        data-testid="delivery-wizard-footer-summary"
      >
        {step === 'decision' ? (
          isReject ? (
            reasonValid ? (
              t('deliveryWizard.footer.reject.summaryValid')
            ) : (
              t('deliveryWizard.footer.reject.summaryInvalid')
            )
          ) : selectedCount > 0 ? (
            <>
              <span className="text-foreground font-semibold">
                {t('deliveryWizard.footer.approve.summaryCount', {
                  selected: selectedCount,
                  total: totalCount,
                })}
              </span>{' '}
              {t('deliveryWizard.footer.approve.summarySuffix')}
            </>
          ) : (
            t('deliveryWizard.footer.approve.summaryEmpty')
          )
        ) : (
          <span className="text-foreground font-semibold">
            {t('deliveryWizard.footer.preview.summary', {
              products: productCount,
              attributes: selectedCount,
              destination: destinationLabel,
            })}
          </span>
        )}
      </p>

      {step === 'decision' ? (
        isReject ? (
          <Button
            type="button"
            variant="destructive"
            className="ml-auto cursor-pointer disabled:cursor-not-allowed sm:ml-0"
            disabled={!reasonValid}
            isLoading={isSubmittingReject}
            onClick={onRejectSubmit}
            data-testid="delivery-wizard-reject-submit-button"
          >
            {t('deliveryWizard.footer.reject.action')}
          </Button>
        ) : (
          <Button
            type="button"
            className="ml-auto cursor-pointer disabled:cursor-not-allowed sm:ml-0"
            disabled={selectedCount === 0}
            onClick={onContinue}
            data-testid="delivery-wizard-next-button"
          >
            {t('deliveryWizard.footer.approve.action')}
          </Button>
        )
      ) : (
        <Button
          type="button"
          className="ml-auto cursor-pointer gap-2 disabled:cursor-not-allowed sm:ml-0"
          disabled={selectedCount === 0 || isConfirming}
          isLoading={isConfirming}
          onClick={onConfirmDeliver}
          data-testid="delivery-wizard-submit-button"
        >
          <Send aria-hidden="true" className="size-4" />
          {t('deliveryWizard.footer.preview.action')}
        </Button>
      )}
    </footer>
  );
}
